import { EmbeddingDeploymentViewModel } from "models/LLMs/EmbeddingDeploymentViewModel";
import { EmbeddingModelViewModel } from "models/LLMs/EmbeddingModelViewModel";
import { LLMDeploymentViewModel } from "models/LLMs/LLMDeploymentViewModel";
import { LLMHostViewModel } from "models/LLMs/LLMHostViewModel";
import {
    CreateEmbeddingModelPayload,
    CreateLLMModelPayload,
    DeploymentConfigTestPayload,
    LLMManagementModelViewModel,
} from "models/LLMs/LLMManagementModelViewModel";
import { TestResultViewModel } from "models/LLMs/TestResultViewModel";
import { BaseService } from "./BaseService";

class LLMManagementService extends BaseService {
    protected urlBase = "api/LLMManagement";

    // Hosts
    public async getHosts(): Promise<LLMHostViewModel[]> {
        return this.get<LLMHostViewModel[]>(new URLSearchParams(), "hosts");
    }

    public async getHost(id: string): Promise<LLMHostViewModel> {
        return this.get<LLMHostViewModel>(
            new URLSearchParams(),
            `hosts/${id}`,
        );
    }

    public async createHost(
        host: Omit<LLMHostViewModel, "id" | "isActive">,
    ): Promise<LLMHostViewModel> {
        return this.post(JSON.stringify(host), new URLSearchParams(), "hosts");
    }

    public async updateHost(
        id: string,
        host: Omit<LLMHostViewModel, "id" | "isActive">,
    ): Promise<LLMHostViewModel> {
        return this.put(
            new URLSearchParams(),
            `hosts/${id}`,
            JSON.stringify(host),
        );
    }

    public async deleteHost(id: string): Promise<Response> {
        return this.delete(new URLSearchParams(), `hosts/${id}`);
    }

    public async testHost(id: string): Promise<TestResultViewModel> {
        return this.post("", new URLSearchParams(), `hosts/${id}/test`);
    }

    // LLM Models
    public async getLLMModels(): Promise<LLMManagementModelViewModel[]> {
        return this.get<LLMManagementModelViewModel[]>(
            new URLSearchParams(),
            "llm-models",
        );
    }

    public async getLLMModel(
        id: string,
    ): Promise<LLMManagementModelViewModel> {
        return this.get<LLMManagementModelViewModel>(
            new URLSearchParams(),
            `llm-models/${id}`,
        );
    }

    public async createLLMModel(
        model: CreateLLMModelPayload,
    ): Promise<LLMManagementModelViewModel> {
        return this.post(
            JSON.stringify(model),
            new URLSearchParams(),
            "llm-models",
        );
    }

    public async updateLLMModel(
        id: string,
        model: Omit<LLMManagementModelViewModel, "id" | "isActive">,
    ): Promise<LLMManagementModelViewModel> {
        return this.put(
            new URLSearchParams(),
            `llm-models/${id}`,
            JSON.stringify(model),
        );
    }

    public async deleteLLMModel(id: string): Promise<Response> {
        return this.delete(new URLSearchParams(), `llm-models/${id}`);
    }

    // LLM Deployments
    public async getLLMDeployments(): Promise<LLMDeploymentViewModel[]> {
        return this.get<LLMDeploymentViewModel[]>(
            new URLSearchParams(),
            "llm-deployments",
        );
    }

    public async createLLMDeployment(
        deployment: Omit<
            LLMDeploymentViewModel,
            "id" | "isActive" | "modelName" | "hostName"
        >,
    ): Promise<LLMDeploymentViewModel> {
        return this.post(
            JSON.stringify(deployment),
            new URLSearchParams(),
            "llm-deployments",
        );
    }

    public async updateLLMDeployment(
        id: string,
        deployment: Omit<
            LLMDeploymentViewModel,
            "id" | "isActive" | "modelName" | "hostName"
        >,
    ): Promise<LLMDeploymentViewModel> {
        return this.put(
            new URLSearchParams(),
            `llm-deployments/${id}`,
            JSON.stringify(deployment),
        );
    }

    public async deleteLLMDeployment(id: string): Promise<Response> {
        return this.delete(new URLSearchParams(), `llm-deployments/${id}`);
    }

    public async testLLMDeployment(
        id: string,
    ): Promise<TestResultViewModel> {
        return this.post(
            "",
            new URLSearchParams(),
            `llm-deployments/${id}/test`,
        );
    }

    public async testLLMDeploymentConfig(
        payload: DeploymentConfigTestPayload,
    ): Promise<TestResultViewModel> {
        return this.post(
            JSON.stringify(payload),
            new URLSearchParams(),
            "llm-deployments/test-config",
        );
    }

    // Embedding Models
    public async getEmbeddingModels(): Promise<EmbeddingModelViewModel[]> {
        return this.get<EmbeddingModelViewModel[]>(
            new URLSearchParams(),
            "embedding-models",
        );
    }

    public async createEmbeddingModel(
        model: CreateEmbeddingModelPayload,
    ): Promise<EmbeddingModelViewModel> {
        return this.post(
            JSON.stringify(model),
            new URLSearchParams(),
            "embedding-models",
        );
    }

    public async updateEmbeddingModel(
        id: string,
        model: Omit<EmbeddingModelViewModel, "id" | "isActive">,
    ): Promise<EmbeddingModelViewModel> {
        return this.put(
            new URLSearchParams(),
            `embedding-models/${id}`,
            JSON.stringify(model),
        );
    }

    public async deleteEmbeddingModel(id: string): Promise<Response> {
        return this.delete(new URLSearchParams(), `embedding-models/${id}`);
    }

    // Embedding Deployments
    public async getEmbeddingDeployments(): Promise<
        EmbeddingDeploymentViewModel[]
    > {
        return this.get<EmbeddingDeploymentViewModel[]>(
            new URLSearchParams(),
            "embedding-deployments",
        );
    }

    public async createEmbeddingDeployment(
        deployment: Omit<
            EmbeddingDeploymentViewModel,
            "id" | "isActive" | "modelName" | "hostName"
        >,
    ): Promise<EmbeddingDeploymentViewModel> {
        return this.post(
            JSON.stringify(deployment),
            new URLSearchParams(),
            "embedding-deployments",
        );
    }

    public async updateEmbeddingDeployment(
        id: string,
        deployment: Omit<
            EmbeddingDeploymentViewModel,
            "id" | "isActive" | "modelName" | "hostName"
        >,
    ): Promise<EmbeddingDeploymentViewModel> {
        return this.put(
            new URLSearchParams(),
            `embedding-deployments/${id}`,
            JSON.stringify(deployment),
        );
    }

    public async deleteEmbeddingDeployment(id: string): Promise<Response> {
        return this.delete(
            new URLSearchParams(),
            `embedding-deployments/${id}`,
        );
    }

    public async testEmbeddingDeployment(
        id: string,
    ): Promise<TestResultViewModel> {
        return this.post(
            "",
            new URLSearchParams(),
            `embedding-deployments/${id}/test`,
        );
    }

    public async testEmbeddingDeploymentConfig(
        payload: DeploymentConfigTestPayload,
    ): Promise<TestResultViewModel> {
        return this.post(
            JSON.stringify(payload),
            new URLSearchParams(),
            "embedding-deployments/test-config",
        );
    }
}

export default LLMManagementService;
